import { Button, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { DownloadIcon } from '@/assets/icons';
import { SUPPORTED_FILE_TYPES } from '@/modules/common/helpers/excel';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';

import { useOrderProfileExcel } from '../hooks/useOrderProfileExcel';
import { OrderProfileFile } from './OrderProfileFile';
import { logger } from '@common/logging/logger';

export function OrderProfileProperties() {
  const { t } = useTranslation('interface');
  const fileInput = useRef(null);
  const { createExcel, downloadExcel, uploadExcel } = useOrderProfileExcel();
  const { showSnackbar } = useSnackbarStore();

  const onFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      // @ts-expect-error strictNullChecks. Pls fix me
      const file = event.target.files[0];
      const buffer = await file.arrayBuffer();
      const result = await uploadExcel(buffer, file.name);
      showSnackbar(t(result.message));
    },
    [showSnackbar, t, uploadExcel],
  );

  const downloadOrderProfile = useCallback(async () => {
    const { activeProject, activeLayoutAlternative } = useAppMetaStore.getState();
    if (activeProject.status !== 'loaded') {
      logger.error('Expected layoutAlternative to be defined');
      return;
    }
    if (activeLayoutAlternative.status !== 'loaded') {
      logger.error('Expected layoutAlternative to be defined');
      return;
    }

    const layoutAlternativeName = activeLayoutAlternative.data.name;
    const projectName = activeProject.data.name;
    const { version } = useFloorplanDesignStore.getState().metaData;

    const blob = await createExcel(projectName, layoutAlternativeName, version);
    downloadExcel(blob, layoutAlternativeName, version);
  }, [createExcel, downloadExcel]);

  return (
    <Stack spacing='10px'>
      <Typography variant='body2' sx={{ fontSize: '10px' }}>
        {t('interface:order_profile.add_description')}
      </Typography>
      <OrderProfileFile />
      <Button
        sx={{
          height: 40,
          textTransform: 'none',
          border: 0,
          '&:hover': { color: 'primary.main', backgroundColor: 'transparent' },
        }}
        onClick={downloadOrderProfile}
        endIcon={<DownloadIcon />}
      >
        {t('interface:order_profile.template')}
      </Button>
      <Button
        sx={{ height: 40 }}
        // @ts-expect-error strictNullChecks. Pls fix me
        onClick={() => fileInput.current.click()}
      >
        {t('interface:order_profile.upload')}
      </Button>
      <input
        type='file'
        accept={SUPPORTED_FILE_TYPES}
        ref={fileInput}
        onChange={onFileSelect}
        hidden
      />
    </Stack>
  );
}
